import { combineLatest as observableCombineLatest, Observable, BehaviorSubject } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { PartialDeep } from 'type-fest';

import { AnswersState, Quiz } from './quiz-answers.js';
import {
    Objectives,
    RawObjectivesState,
    NotificationMessage,
    TimestampState,
    BooleanState,
    CompletionRecord,
    CompletionState
} from './objectives.js';
import { Configuration } from './configuration.js';
import { ConnectionViolationReason } from './ws-status.js';
import { AuditScoreState, AuditScore } from './audit-score.js';
import { Bonus, AnyPenalty } from './bonus.js';
import { arrayCompare, deepFreeze, objectCompare } from './util.js';
import { FndnInstanceInfo } from './fndn-status.js';
import { TrainingSessionId } from './training-session-transport.js';
import { TeamId } from './team-id.js';
import { shareConfigPublishReplayRefCount } from './share-config-publish-replay.js';
import { ObjectiveConfig } from './objective-config.js';
import { DeviceStatusResult } from '@ffc/shared';

export interface FortigateDetails {
    serial: string;
    // local hostname for the fortigate
    hostname: string;
    // public ip for the fortigate
    ip: string;
    // public FQDN
    fqdn?: string;
    // last time we checked the public fqdn
    fqdnUpdatedTimestamp?: number;
}

// @typescript-to-gdscript-skip
export type AnyTeamState = TeamState | CensoredTeamState | PrunedTeamState;

/**
 * Lifecycle status of a team.
 * - PENDING: unnamed placeholder with no pre-assigned members. Truly unclaimed —
 *   any player can claim and rename it (see isClaimablePremade).
 * - ASSIGNED: has pre-assigned members (via admin x-codes / FNDN teammate
 *   pre-assignment) but is not yet named. Reserved for those specific players —
 *   NOT claimable by arbitrary players. Only the assigned members can complete
 *   the claim by naming the team (which transitions it to VALID).
 * - VALID: claimed (has name + members), no connection violation. Leaderboard-eligible.
 * - INVALID: has both a name and members AND an active connection violation. Still
 *   shown on the leaderboard so infractions are visible, but flagged. Recoverable:
 *   flips back to VALID when the violation clears (see computeTeamStatus). Teams
 *   without both a name and members suppress the violation flag — there is no real
 *   team yet to display an infraction for.
 */
// @typescript-to-gdscript-skip leaderboard-only
export type TeamStatus = 'PENDING' | 'ASSIGNED' | 'VALID' | 'INVALID';

/**
 * Derives the TeamStatus from the load-bearing fields. This is the single source of
 * truth for status transitions — call it whenever name/members/hasConnectionViolation
 * may have changed (e.g. in updateTeam, recheckConnectionViolation, upgrade).
 */
export function computeTeamStatus(
    team: Pick<TeamState, 'name' | 'members' | 'hasConnectionViolation'>
): TeamStatus {
    const hasMembers = !!(team.members && team.members.length > 0);
    const hasName = !!team.name;
    if (!hasMembers && !hasName) return 'PENDING';
    // Unnamed but with pre-assigned members: reserved for those players, not
    // claimable by arbitrary players (isClaimablePremade requires empty members).
    if (hasMembers && !hasName) return 'ASSIGNED';
    if (!hasMembers && hasName) return 'PENDING';
    if (team.hasConnectionViolation) return 'INVALID';
    return 'VALID';
}

/**
 * Censored team state, omits any sensitive information that should not be sent to
 * unauthorized users.
 */
export interface CensoredTeamState {
    // TODO: determine why TeamId doesn't work despite this fix:
    // https://github.com/jamie-pate/typescript-to-gdscript/pull/2
    // @typescript-to-gdscript-type: int
    readonly id: number; // TeamId;
    name: string;
    members: string[];
    countries: string[];
}

/**
 * Pruned team state for the escaperoom
 */

// @typescript-to-gdscript-skip
export interface EscapeRoomTeamStateExceptAnswers extends CensoredTeamState {
    readonly id: TeamId;
    name: string;
    expectedMemberCount: number;
    // stores the email addresses of the team members
    // Written by both admin assignment and login — NOT a reliable admin-assignment signal.
    members: string[];
    auditScore: AuditScoreState;
    fortigateSn: string;
    // multiFabric list of fortigate serial numbers
    fortigates: FortigateDetails[];
    fortigateIp: string;
    previousScore: number;
    code: string;
    instanceHeartbeat: number | null;
    countries: string[];
    teamNotifications: NotificationMessage[];
    assignedXCodes?: string[];
    // Admin-assigned emails only (not login). Read by xcodeAssignedToTeam at connect.
    // @typescript-to-gdscript-skip leaderboard-only
    assignedEmails?: string[];
    // @typescript-to-gdscript-skip leaderboard-only
    hasConnectionViolation?: boolean;
    // @typescript-to-gdscript-skip leaderboard-only
    connectionViolationReasons?: ConnectionViolationReason[];
    // @typescript-to-gdscript-skip leaderboard-only
    status?: TeamStatus;
}

// @typescript-to-gdscript-skip
export interface EscapeRoomTeamState extends EscapeRoomTeamStateExceptAnswers {
    answers: Record<string, string>;
}

/**
 * Pruned team state, this can be sent across the network if users are authorized
 */

// @typescript-to-gdscript-skip
export interface PrunedTeamState extends EscapeRoomTeamStateExceptAnswers {
    readonly id: TeamId;
    name: string;
    expectedMemberCount: number;
    members: string[];
    auditScore: AuditScoreState;
    fortigateSn: string;
    // multiFabric list of fortigate serial numbers
    fortigates: FortigateDetails[];
    fortigateIp: string;
    objectives: CompletionRecord;
    startedObjectiveGroups: TimestampState;
    answers: AnswersState;
    previousScore: number;
    code: string;
    instanceHeartbeat: number | null;
    countries: string[];
    webhookObjectives: CompletionRecord;
    teamNotifications: NotificationMessage[];
    securityReportObjectives: Record<string, RawObjectivesState>;
    // TODO: make mandatory when rr types become synced via ffc/shared
    validationErrors?: string[];
    deviceStatuses: DeviceStatusResult[];
    rrWsConnected: string;
    rrWsDisconnected: string;
    restartRequestedAt: string;
    restartAckedAt: string;
    restartCompletedAt: string;
    webhookResponses?: Record<string, WebhookResponse>;
    // clues picked up by the team during an escape room.
    // Only consumed by the admin UI (team-details dialog) via pruneTeamProgress path.
    // NOT sent to the game client through this path — the game client receives
    // clue data via the separate WebSocket EscapeRoomState channel instead.
    collectedClues?: CollectedClue[];
}

/**
 * Stores the response from a webhook trigger
 */
// @typescript-to-gdscript-skip unknown doesn't work in ts-to-gdscript
export interface WebhookResponse {
    timestamp: number;
    response: unknown;
    errors?: string[];
}

/**
 * Clue metadata stored server-side when a team picks up a clue.
 * Stored as an array — the id field carries the clue identifier.
 * Properties are readonly because clue data is assigned as a whole object
 * by the GDScript client, never mutated in place.
 */
// @typescript-to-gdscript-skip
export interface CollectedClue {
    readonly id: string;
    readonly text: string;
    readonly imageUrl?: string;
    readonly copyText?: string;
    readonly type: string;
    readonly locationId: string;
    readonly locationName: string;
}

/**
 * Full internal team state. While this interface extends PrunedTeamState and thus
 * inherits collectedClues, the game client does NOT receive clue data through this type.
 * The game client receives clue data through the EscapeRoomState WebSocket channel.
 * collectedClues on PrunedTeamState is consumed by the admin UI via pruneTeamProgress.
 */

// @typescript-to-gdscript-skip
export interface TeamState extends PrunedTeamState {
    leaderboardObjectives: RawObjectivesState;
    fndnInfo: FndnInstanceInfo | null;
}

// @typescript-to-gdscript-skip
export interface SessionAndTeamState extends TeamState {
    readonly sessionId: TrainingSessionId;
}

export const DEFAULT_TEAM_STATE: TeamState = deepFreeze({
    id: 0,
    name: '',
    code: '',
    fortigateSn: 'x',
    fortigateIp: '',
    fortigates: [{ serial: 'x', hostname: '', ip: '' }],
    startedObjectiveGroups: {},
    securityReportObjectives: {},
    objectives: {},
    leaderboardObjectives: {},
    webhookObjectives: {},
    answers: {},
    auditScore: {
        passed: 0,
        low: 0,
        medium: 0,
        high: 0,
        score: 0
    },
    deviceStatuses: [],
    previousScore: 0,
    members: [],
    teamNotifications: [],
    countries: [],
    expectedMemberCount: 1,
    fndnInfo: null,
    instanceHeartbeat: null,
    rrWsConnected: '',
    rrWsDisconnected: '',
    restartRequestedAt: '',
    restartAckedAt: '',
    restartCompletedAt: '',
    webhookResponses: {},
    hasConnectionViolation: false,
    connectionViolationReasons: [],
    status: 'PENDING'
});

export class Team {
    private _auditScore: BehaviorSubject<AuditScore>;
    private _members: BehaviorSubject<string[]>;
    private _objectives: BehaviorSubject<Objectives>;
    private _deviceStatuses: BehaviorSubject<DeviceStatusResult[]>;
    private _answers: BehaviorSubject<Quiz>;
    private _bonuses: BehaviorSubject<Bonus[]>;
    private _penalties: BehaviorSubject<AnyPenalty[]>;
    private _teamNotifications: BehaviorSubject<NotificationMessage[]>;

    auditScore: Observable<AuditScore>;
    totalScore: Observable<number>;
    members: Observable<string[]>;
    objectives: Observable<Objectives>;
    answers: Observable<Quiz>;
    bonuses: Observable<Bonus[]>;
    penalties: Observable<AnyPenalty[]>;
    bonusScore: Observable<number>;
    penaltyScore: Observable<number>;
    previousScore: number;
    fortigateSn: string;
    collectedClues: CollectedClue[] = [];

    constructor(
        public id: TeamId,
        public name: string,
        public expectedMemberCount: number,
        members: string[],
        auditScore: AuditScore,
        fortigateSn: string,
        public fortigates: FortigateDetails[],
        public fortigateIp: string,
        objectives: Objectives,
        answers: Quiz,
        private startedObjectiveGroups: TimestampState,
        private securityReportObjectives: Record<string, RawObjectivesState>,
        private leaderboardObjectives: RawObjectivesState,
        config: Configuration,
        private code: string,
        public fndnInfo: FndnInstanceInfo | null,
        public instanceHeartbeat: number | null,
        public rrWsConnected: string,
        public rrWsDisconnected: string,
        public restartRequestedAt: string,
        public restartAckedAt: string,
        public restartCompletedAt: string,
        public webhookObjectives: Objectives,
        public countries: string[],
        public teamNotifications: NotificationMessage[] = [],
        public validationErrors?: string[],
        public webhookResponses: Record<string, WebhookResponse> = {}
    ) {
        this._members = new BehaviorSubject<string[]>(members);
        this._auditScore = new BehaviorSubject<AuditScore>(auditScore);
        this.auditScore = this._auditScore.asObservable();
        this._objectives = new BehaviorSubject<Objectives>(objectives);
        this._answers = new BehaviorSubject<Quiz>(answers);
        this._bonuses = new BehaviorSubject<Bonus[]>([]);
        this._penalties = new BehaviorSubject<AnyPenalty[]>([]);
        this._teamNotifications = new BehaviorSubject<NotificationMessage[]>(teamNotifications);
        this._deviceStatuses = new BehaviorSubject<DeviceStatusResult[]>([]);

        this.members = this._members.asObservable();
        this.bonusScore = this._bonuses.pipe(
            map(bonuses => bonuses.reduce((s, b) => s + b.score(config), 0))
        );
        this.penaltyScore = this._penalties.pipe(
            map(penalties => penalties.reduce((s, p) => s + p.score(config), 0))
        );
        this.previousScore = 0;

        this.totalScore = observableCombineLatest([
            this._auditScore.asObservable(),
            this._objectives.asObservable(),
            this._answers.asObservable(),
            this.bonusScore,
            this.penaltyScore
        ]).pipe(
            map(
                ([s, o, a, b, p]) =>
                    s.score + o.score(config.objectives) + a.score(config.answers) + b + p
            ),
            share(shareConfigPublishReplayRefCount())
        );

        this.objectives = this._objectives.asObservable();
        this.answers = this._answers.asObservable();
        this.bonuses = this._bonuses.asObservable();
        this.penalties = this._penalties.asObservable();
        this.fortigateSn = fortigateSn;
    }

    static upgrade(teamState: TeamState): TeamState {
        if (!teamState.fortigates || teamState.fortigates.length < 1) {
            teamState.fortigates = [
                {
                    ip: teamState.fortigateIp,
                    serial: teamState.fortigateSn,
                    hostname: ''
                }
            ];
        }
        teamState.securityReportObjectives = teamState.securityReportObjectives || {};
        teamState.fortigates = teamState.fortigates.map(f =>
            typeof f === 'string' ? { ip: '', serial: f, hostname: '' } : f
        );
        teamState.hasConnectionViolation = teamState.hasConnectionViolation ?? false;
        teamState.connectionViolationReasons = teamState.connectionViolationReasons ?? [];

        if (!teamState.webhookObjectives) {
            teamState.webhookObjectives = {};
            teamState.status = teamState.status ?? computeTeamStatus(teamState);
            return { ...teamState };
        }
        if (!teamState.startedObjectiveGroups) {
            teamState.startedObjectiveGroups = {};
        }
        teamState.status = teamState.status ?? computeTeamStatus(teamState);
        if (!teamState.collectedClues) {
            teamState.collectedClues = [];
        }
        return teamState;
    }

    static fromState(
        teamState: TeamState,
        config: Configuration,
        bonuses: Bonus[] | null = null,
        penalties?: AnyPenalty[]
    ) {
        const result = new Team(
            teamState.id,
            teamState.name,
            teamState.expectedMemberCount,
            teamState.members || [],
            AuditScore.fromState(teamState.auditScore || AuditScore.zero),
            teamState.fortigateSn,
            teamState.fortigates || [],
            teamState.fortigateIp,
            Objectives.fromState(teamState.objectives || {}),
            Quiz.fromState(teamState.answers || {}),
            teamState.startedObjectiveGroups || {},
            teamState.securityReportObjectives || {},
            teamState.leaderboardObjectives || {},
            config,
            teamState.code,
            teamState.fndnInfo,
            teamState.instanceHeartbeat,
            teamState.rrWsConnected,
            teamState.rrWsDisconnected,
            teamState.restartRequestedAt,
            teamState.restartAckedAt,
            teamState.restartCompletedAt,
            Objectives.fromState(teamState.webhookObjectives || {}),
            teamState.countries?.slice() || [],
            teamState.teamNotifications,
            teamState.validationErrors,
            teamState.webhookResponses || {}
        );
        if (bonuses || penalties) {
            result.updateBonuses(bonuses, penalties);
        }
        if (!result.teamNotifications) {
            result.teamNotifications = [];
        }
        result.collectedClues = teamState.collectedClues ? teamState.collectedClues.slice() : [];
        return result;
    }

    static censorState(teamState: AnyTeamState): CensoredTeamState {
        return {
            id: teamState.id,
            members: teamState.members.slice(),
            name: teamState.name,
            countries: teamState.countries?.slice() || []
        };
    }

    static pruneState(ts: CensoredTeamState): CensoredTeamState;
    static pruneState(ts: PartialDeep<PrunedTeamState>): PartialDeep<PrunedTeamState>;
    static pruneState(ts: TeamState | PrunedTeamState): PrunedTeamState;
    static pruneState<TS extends TeamState | PartialDeep<TeamState>>(
        ts: TS
    ): Pick<TS, keyof PrunedTeamState> {
        // Returning a literal is the only way to engage typescript's stricter property checks
        return {
            id: ts.id,
            name: ts.name,
            members: ts.members?.slice(),
            fortigateSn: ts.fortigateSn,
            countries: ts.countries,
            expectedMemberCount: ts.expectedMemberCount,
            auditScore: ts.auditScore && Object.assign({}, ts.auditScore),
            fortigates: ts.fortigates && ts.fortigates.slice(),
            fortigateIp: ts.fortigateIp && ts.fortigateIp,
            startedObjectiveGroups: ts.startedObjectiveGroups,
            objectives: ts.objectives && Object.assign({}, ts.objectives),
            answers: ts.answers && Object.assign({}, ts.answers),
            previousScore: ts.previousScore,
            code: ts.code,
            instanceHeartbeat: ts.instanceHeartbeat,
            rrWsConnected: ts.rrWsConnected,
            rrWsDisconnected: ts.rrWsDisconnected,
            restartRequestedAt: ts.restartRequestedAt,
            restartAckedAt: ts.restartAckedAt,
            restartCompletedAt: ts.restartCompletedAt,
            webhookObjectives: ts.webhookObjectives && Object.assign({}, ts.webhookObjectives),
            teamNotifications: ts.teamNotifications,
            securityReportObjectives: ts.securityReportObjectives,
            deviceStatuses: ts.deviceStatuses,
            validationErrors: ts.validationErrors,
            webhookResponses: ts.webhookResponses && Object.assign({}, ts.webhookResponses),
            hasConnectionViolation: ts.hasConnectionViolation,
            connectionViolationReasons: ts.connectionViolationReasons?.map(r => ({
                ...r,
                ips: r.ips?.slice()
            })),
            status: ts.status,
            collectedClues: ts.collectedClues ? ts.collectedClues.slice() : undefined
        };
    }

    static getObjectivesWithTimestamps(
        team: TeamState,
        objectives: ObjectiveConfig[],
        currentObjectives: BooleanState,
        timestamp: number
    ): CompletionRecord {
        const result: CompletionRecord = {};
        for (const o of objectives) {
            const completed: boolean = currentObjectives[o.id] || false;
            const wasCompleted: CompletionState | undefined = team.objectives[o.id];
            if (completed) {
                if (!wasCompleted?.completed) {
                    result[o.id] = {
                        completed: timestamp,
                        firstCompleted: wasCompleted?.firstCompleted || timestamp
                    };
                } else {
                    result[o.id] = wasCompleted;
                }
            } else {
                if (o.id in team.objectives && o.sticky) {
                    result[o.id] = wasCompleted;
                } else {
                    result[o.id] = { completed: 0, firstCompleted: wasCompleted?.firstCompleted };
                }
            }
        }
        return result;
    }

    toState(): TeamState {
        return {
            id: this.id,
            name: this.name,
            members: this._members.value,
            expectedMemberCount: this.expectedMemberCount,
            auditScore: this._auditScore.value,
            deviceStatuses: this._deviceStatuses.value,
            fortigateSn: this.fortigateSn,
            fortigates: (this.fortigates || []).slice(),
            fortigateIp: this.fortigateIp,
            objectives: this._objectives.value.toState(),
            startedObjectiveGroups: this.startedObjectiveGroups,
            securityReportObjectives: this.securityReportObjectives,
            leaderboardObjectives: this.leaderboardObjectives,
            answers: this._answers.value.toState(),
            previousScore: this.previousScore,
            code: this.code,
            fndnInfo: this.fndnInfo,
            instanceHeartbeat: this.instanceHeartbeat,
            rrWsConnected: this.rrWsConnected,
            rrWsDisconnected: this.rrWsDisconnected,
            restartRequestedAt: this.restartRequestedAt,
            restartAckedAt: this.restartAckedAt,
            restartCompletedAt: this.restartCompletedAt,
            webhookObjectives: this.webhookObjectives.toState(),
            countries: this.countries,
            teamNotifications: this._teamNotifications?.value,
            validationErrors: this.validationErrors,
            webhookResponses: this.webhookResponses || {},
            collectedClues: this.collectedClues ? this.collectedClues.slice() : []
        };
    }

    addTeamNotification(message: NotificationMessage) {
        this._teamNotifications.value.concat([message]);
        this._teamNotifications.next(this._teamNotifications.value);
    }

    /**
     * Partially compare two team states to see if they have been updated.
     * Usually used for view updates, so internal stuff is ignored.
     * @param a TeamState a.
     * @param b TeamState b.
     * @returns True if a and b differ enough that we would want to update the display.
     */
    static viewStateEqual<T extends AnyTeamState>(anyA: T, anyB: T) {
        const a = anyA as TeamState;
        const b = anyB as TeamState;
        const keys = ['id', 'name', 'fortigateIp', 'fortigateSn'] as Array<keyof TeamState>;
        return (
            keys.every(k => a[k] === b[k]) &&
            objectCompare(a.auditScore, b.auditScore) &&
            objectCompare(a.objectives, b.objectives) &&
            objectCompare(a.answers, b.answers) &&
            arrayCompare(a.members, b.members) &&
            arrayCompare(a.countries, b.countries) &&
            arrayCompare(a.fortigates, b.fortigates, (fga, fgb) => objectCompare(fga, fgb)) &&
            a.hasConnectionViolation === b.hasConnectionViolation &&
            arrayCompare(
                a.connectionViolationReasons as unknown as Record<string, any>[],
                b.connectionViolationReasons as unknown as Record<string, any>[],
                (ra, rb) => objectCompare(ra, rb)
            ) &&
            a.status === b.status
        );
    }

    update(other: Team, bonuses: Bonus[], penalties: AnyPenalty[]) {
        if (other) {
            (
                [
                    'id',
                    'name',
                    'fortigateSn',
                    'fndnInfo',
                    'code',
                    'secret',
                    'collectedClues'
                ] as Array<keyof Team>
            ).forEach(k => ((<any>this[k]) = <any>other[k]));
            if (this._auditScore.value !== other._auditScore.value) {
                this._auditScore.next(other._auditScore.value);
            }
            if (!this._objectives.value.equals(other._objectives.value)) {
                this._objectives.next(other._objectives.value);
            }
            if (!this._answers.value.equals(other._answers.value)) {
                this._answers.next(other._answers.value);
            }
            if (!arrayCompare(this._members.value, other._members.value)) {
                this._members.next(other._members.value);
            }
            if (this._teamNotifications.value.length !== other._teamNotifications.value.length) {
                this._teamNotifications.next(other._teamNotifications.value);
            }
        }
        this.updateBonuses(bonuses, penalties);
    }

    private updateBonuses(bonuses: Bonus[] | null = null, penalties: AnyPenalty[] | null = null) {
        if (bonuses) {
            bonuses = bonuses.filter(b => b && b.team && b.team.id === this.id);
            if (
                this._bonuses.value.length !== bonuses.length ||
                !this._bonuses.value.every((b, i) => b.equals(bonuses![i]))
            ) {
                this._bonuses.next(bonuses);
            }
        }
        if (penalties) {
            penalties = penalties.filter(p => p && p.team && p.team.id === this.id);
            if (
                this._penalties.value.length !== penalties.length ||
                !this._penalties.value.every((p, i) => p.equals(penalties![i]))
            ) {
                this._penalties.next(penalties);
            }
        }
    }
}
